package org.teddapp.project.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsFocusedAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.LocalTextStyle
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.text.input.VisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp

/**
 * Champ du design system : libellé en casse de phrase, surface contourée, bordure d'encre
 * et halo citron vert doux quand le champ a le focus.
 */
@Composable
fun TeddField(
    label: String,
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    keyboardType: KeyboardType = KeyboardType.Text,
    visualTransformation: VisualTransformation = VisualTransformation.None,
    leadingIcon: (@Composable () -> Unit)? = null,
    trailingContent: (@Composable () -> Unit)? = null,
    placeholder: String? = null,
) {
    val interaction = remember { MutableInteractionSource() }
    val focused by interaction.collectIsFocusedAsState()
    val shape = RoundedCornerShape(TeddTokens.RSm)
    Column(modifier = modifier) {
        Text(
            text = label,
            style = TeddType.Label,
            color = TeddTokens.Ink,
            modifier = Modifier.padding(bottom = 6.dp),
        )
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(TeddSize.ControlMd + 6.dp)
                // Halo de focus : trois points de citron vert doux autour du champ.
                .then(if (focused) Modifier.border(3.dp, TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RSm + 3.dp)).padding(3.dp) else Modifier.padding(3.dp))
                .background(TeddTokens.Surface, shape)
                .border(1.dp, if (focused) TeddTokens.Ink else TeddTokens.LineStrong, shape)
                .padding(horizontal = TeddSpace.S3),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            if (leadingIcon != null) {
                Box(modifier = Modifier.size(14.dp), contentAlignment = Alignment.Center) { leadingIcon() }
            }
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                modifier = Modifier.weight(1f),
                singleLine = true,
                interactionSource = interaction,
                textStyle = TeddType.Body.copy(color = TeddTokens.Ink),
                cursorBrush = SolidColor(TeddTokens.Ink),
                keyboardOptions = KeyboardOptions(keyboardType = keyboardType),
                visualTransformation = visualTransformation,
                decorationBox = { inner ->
                    if (value.isEmpty() && placeholder != null) {
                        Text(text = placeholder, color = TeddTokens.InkSubtle, style = TeddType.Body, maxLines = 1)
                    }
                    inner()
                },
            )
            if (trailingContent != null) trailingContent()
        }
    }
}

/** Masquage d'un mot de passe. */
fun teddPasswordTransformation(): VisualTransformation = PasswordVisualTransformation()
